import { CareerSiteFiltersOption } from 'api-clients/monolith';
import { AppContext } from 'contexts/v2/app-context';
import { LocationIcon } from 'icons/location-icon';
import { MonitorIcon } from 'icons/monitor-icon';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSearchBar } from 'hooks/v2/useSearchBar';

import { JobSearchInput } from './job-search-input';
import { LocationInput } from './location-input';
import { RadiusSelect } from './radius-select';
import { SearchButton } from './search-button';

interface SearchBarGroupProps {
  scrollToJobs: () => void;
  radiusOptions: CareerSiteFiltersOption[];
}

export function SearchBarGroup({
  scrollToJobs,
  radiusOptions,
}: SearchBarGroupProps) {
  const intl = useIntl();

  const {
    setJobSearchQuery,
    jobSearchQuery,
    resetFilters,
    setLocation,
    setPlaceId,
    setIsJobsFromCurrentLocation,
    location,
    updateUrlWithParams,
    accountSlug,
    brandId,
    radius,
    setRadius,
    setPage,
    currentCity,
    currentState,
    currentCountry,
    displayLocation,
    setDisplayLocation,
    setIsFetchingJobs,
  } = useContext(AppContext);

  const remoteKeywords = intl
    .formatMessage({
      defaultMessage: 'remote,work from home,wfh,telecommute',
      description:
        'Search keywords for remote work locations (comma-separated, will be split)',
    })
    .split(',')
    .map(k => k.trim().toLowerCase());

  const remoteLabel = intl.formatMessage({
    defaultMessage: 'Remote',
    description: 'Display label for remote work location option',
  });

  const currentLocationLabel = intl.formatMessage({
    defaultMessage: 'Current Location',
    description: 'Filter option for current location',
  });

  const {
    openJobPopover,
    setOpenJobPopover,
    openLocationPopover,
    setOpenLocationPopover,
    jobSearch,
    setJobSearch,
    locationSearch,
    setLocationSearch,
    jobInputRef,
    locationInputRef,
    isLoading,
    setIsLoading,
  } = useSearchBar({
    initialLocationSearch:
      displayLocation ??
      (location.value === 'all'
        ? ''
        : location.value === 'remote'
          ? remoteLabel
          : location.value === 'current_location'
            ? currentLocationLabel
            : (location.value ?? '')),
  });

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const [localRadius, setLocalRadius] =
    useState<CareerSiteFiltersOption | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!location.value) {
      setLocationSearch('');
      // If user manually cleared the location input, normalize to global search
      if (accountSlug) {
        setLocation({ id: null, value: 'all' });
        setPlaceId(null);
        setIsJobsFromCurrentLocation(false);
        setIsFetchingJobs(true);
        updateUrlWithParams(accountSlug, brandId, 'location', 'all');
        updateUrlWithParams(accountSlug, brandId, 'radius', '');
        updateUrlWithParams(accountSlug, brandId, 'page', '1');
      }
      return;
    }

    if (location.value === 'remote') {
      setLocationSearch(prev =>
        prev && prev.toLowerCase() !== 'remote' ? prev : remoteLabel,
      );
      return;
    }
    if (location.value === 'current_location') {
      if (displayLocation) {
        setLocationSearch(displayLocation);
      } else {
        const parts = [currentCity, currentState, currentCountry].filter(
          Boolean,
        );
        const fullLocation =
          parts.length > 0 ? parts.join(', ') : currentLocationLabel;
        setLocationSearch(fullLocation);
      }
      return;
    }

    setLocationSearch(location.value === 'all' ? '' : location.value);
  }, [
    location.value,
    displayLocation,
    currentCity,
    currentState,
    currentCountry,
    remoteLabel,
    currentLocationLabel,
    accountSlug,
    brandId,
    setIsFetchingJobs,
    setIsJobsFromCurrentLocation,
    setLocation,
    setLocationSearch,
    setPlaceId,
    updateUrlWithParams,
  ]);

  useEffect(() => {
    if (jobSearchQuery) {
      setJobSearch(jobSearchQuery);
    }
  }, [jobSearchQuery, setJobSearch]);

  useEffect(() => {
    if (
      radiusOptions &&
      Array.isArray(radiusOptions) &&
      radiusOptions.length > 0 &&
      !hasInitialized.current
    ) {
      if (radius) {
        setLocalRadius(radius);
      } else {
        const defaultRadius =
          radiusOptions.find(opt => opt.key.startsWith('50')) ??
          radiusOptions[0];
        setLocalRadius(defaultRadius);
      }
      hasInitialized.current = true;
    }
  }, [radiusOptions, radius]);

  useEffect(() => {
    const isGlobalSearch = location.value === 'all';
    const isCurrentLocation = location.value === 'current_location';

    const hasCurrentLocationData =
      isCurrentLocation && currentCity && currentState && currentCountry;

    const hasLocation =
      !isCurrentLocation && locationSearch && locationSearch.length > 0;
    const hasRadius = radiusOptions && radiusOptions.length > 0;

    const shouldWaitForLocation = true;

    if (
      (hasLocation && hasRadius) ||
      isGlobalSearch ||
      (shouldWaitForLocation && hasCurrentLocationData) ||
      (!shouldWaitForLocation && hasRadius)
    ) {
      setIsLoading(false);
    }
  }, [
    locationSearch,
    localRadius,
    location.value,
    currentCity,
    currentState,
    currentCountry,
    isLoading,
    currentLocationLabel,
    remoteLabel,
    radiusOptions,
    setIsLoading,
  ]);

  const searchLower = locationSearch.toLowerCase().trim();
  const shouldShowRemote =
    searchLower.length > 0 &&
    remoteKeywords.some(keyword => keyword.startsWith(searchLower));

  const specialLocations = [] as Array<{
    id: string;
    name: string;
    type: 'remote' | 'current';
    icon?: React.ReactNode;
    sublabel?: string;
  }>;

  const shouldShowCurrentLocation = locationSearch.trim() === '';

  if (shouldShowCurrentLocation) {
    const buildCurrentLocationString = () => {
      const parts = [currentCity, currentState, currentCountry].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : 'Use my current location';
    };

    specialLocations.push({
      id: 'current-location',
      name: currentLocationLabel,
      type: 'current',
      sublabel: buildCurrentLocationString(),
      icon: <LocationIcon />,
    });
  }

  if (shouldShowRemote) {
    specialLocations.push({
      id: 'remote',
      name: remoteLabel,
      type: 'remote' as const,
      icon: <MonitorIcon />,
    });
  }

  const handleLocationSelect = (id: string, value: string) => {
    setSelectedLocationId(id);
    setLocationSearch(value);
    setOpenLocationPopover(false);

    const isRemote = id === 'remote';
    const isCurrent = id === 'current-location';

    resetFilters();

    if (isRemote) {
      // For Remote, store "remote" (hardcoded) in context for URL/backend
      // but keep translated label in locationSearch for display
      setLocation({
        id: 'remote',
        value: 'remote', // Hardcoded for backend/URL
      });
      setPlaceId(null);
    } else if (isCurrent) {
      // Current location special option
      const parts = [currentCity, currentState, currentCountry].filter(Boolean);
      const fullLocation = parts.join(', ');
      setLocation({ id: null, value: fullLocation || 'current_location' });
      setPlaceId(null);
      if (fullLocation) setDisplayLocation(fullLocation);
    } else {
      // Regular location
      setLocation({
        id,
        value,
      });
      setPlaceId(id);
    }

    setIsJobsFromCurrentLocation(isCurrent);
    setIsFetchingJobs(true);

    if (accountSlug) {
      if (jobSearch) {
        updateUrlWithParams(accountSlug, brandId, 'query', jobSearch);
      }

      if (isRemote) {
        updateUrlWithParams(accountSlug, brandId, 'location', 'remote');
      } else if (isCurrent) {
        const parts = [currentCity, currentState, currentCountry].filter(
          Boolean,
        );
        const fullLocation = parts.join(', ');
        if (fullLocation) {
          updateUrlWithParams(accountSlug, brandId, 'location', fullLocation);
        }
      } else {
        updateUrlWithParams(accountSlug, brandId, 'location', value);
      }

      if (!isRemote) {
        const radiusToPersist =
          radius ?? localRadius ?? radiusOptions[0] ?? null;
        if (radiusToPersist) {
          setRadius(radiusToPersist);
          updateUrlWithParams(
            accountSlug,
            brandId,
            'radius',
            radiusToPersist.key,
          );
        }
      }

      updateUrlWithParams(accountSlug, brandId, 'page', '1');
    }
  };

  const handleJobSelect = (value: string) => {
    setJobSearch(value);
    setOpenJobPopover(false);

    // Auto-search when job is selected from dropdown
    resetFilters();
    setJobSearchQuery(value);

    if (selectedLocationId && locationSearch) {
      setLocation({
        id: selectedLocationId,
        value: locationSearch,
      });
      setPlaceId(selectedLocationId);
      setIsJobsFromCurrentLocation(false);
    } else if (locationSearch && !selectedLocationId) {
      setLocation({
        id: null,
        value: locationSearch,
      });
      setIsJobsFromCurrentLocation(false);
    } else if (!locationSearch) {
      setLocation({
        id: null,
        value: 'all',
      });
      setIsJobsFromCurrentLocation(false);
    }

    if (accountSlug) {
      updateUrlWithParams(accountSlug, brandId, 'query', value);

      if (locationSearch) {
        updateUrlWithParams(accountSlug, brandId, 'location', locationSearch);
        if (radius && radius.key) {
          updateUrlWithParams(accountSlug, brandId, 'radius', radius.key);
        }
      } else {
        updateUrlWithParams(accountSlug, brandId, 'location', 'all');
      }

      updateUrlWithParams(accountSlug, brandId, 'page', '1');
    }
  };

  const clearJobSearch = () => {
    const isActiveSearch = jobSearch === jobSearchQuery;

    setJobSearch('');
    setOpenJobPopover(false);

    if (isActiveSearch) {
      setJobSearchQuery(null);

      if (accountSlug) {
        updateUrlWithParams(accountSlug, brandId, 'query', '');
        updateUrlWithParams(accountSlug, brandId, 'page', '1');

        if (location && location.value && location.value !== 'all') {
          updateUrlWithParams(accountSlug, brandId, 'location', location.value);
        }

        if (radius && radius.key) {
          updateUrlWithParams(accountSlug, brandId, 'radius', radius.key);
        }
      }
    }
  };

  const clearLocationSearch = () => {
    const normalize = (s: string | null | undefined) =>
      (s ?? '').toLowerCase().trim();
    const isRemoteActive =
      normalize(location?.value) === 'remote' ||
      normalize(locationSearch) === 'remote';
    const isCurrentActive =
      normalize(location?.value) === 'current_location' ||
      normalize(locationSearch) === normalize(currentLocationLabel);
    const isActiveSearch =
      normalize(locationSearch) === normalize(location.value) ||
      (locationSearch && location.value === 'all') ||
      isRemoteActive ||
      isCurrentActive;

    setLocationSearch('');
    setOpenLocationPopover(false);
    setSelectedLocationId(null);

    if (isActiveSearch) {
      resetFilters();
      setLocation({ id: null, value: 'all' });
      setPlaceId(null);
      setIsJobsFromCurrentLocation(false);
      setIsFetchingJobs(true);

      if (accountSlug) {
        updateUrlWithParams(accountSlug, brandId, 'location', 'all');
        updateUrlWithParams(accountSlug, brandId, 'page', '1');
        updateUrlWithParams(accountSlug, brandId, 'radius', '');

        if (jobSearchQuery) {
          updateUrlWithParams(accountSlug, brandId, 'query', jobSearchQuery);
        }
      }
    }
  };

  const handleSearch = () => {
    resetFilters();
    setJobSearchQuery(jobSearch);

    const isRemoteSearch = remoteKeywords.some(
      keyword => locationSearch?.toLowerCase() === keyword,
    );

    if (isRemoteSearch) {
      setLocation({
        id: 'remote',
        value: 'remote',
      });
      setPlaceId(null);
      setIsJobsFromCurrentLocation(false);
    } else if (selectedLocationId && locationSearch) {
      setLocation({
        id: selectedLocationId,
        value: locationSearch,
      });
      setPlaceId(selectedLocationId);
      setIsJobsFromCurrentLocation(false);
    } else if (locationSearch && !selectedLocationId) {
      setLocation({
        id: null,
        value: locationSearch,
      });
      setIsJobsFromCurrentLocation(false);
    } else if (!locationSearch) {
      setLocation({
        id: null,
        value: 'all',
      });
      setIsJobsFromCurrentLocation(false);
    }

    if (accountSlug) {
      if (jobSearch) {
        updateUrlWithParams(accountSlug, brandId, 'query', jobSearch);
      }

      if (isRemoteSearch) {
        updateUrlWithParams(accountSlug, brandId, 'location', 'remote');
        updateUrlWithParams(accountSlug, brandId, 'radius', '');
      } else if (locationSearch) {
        updateUrlWithParams(accountSlug, brandId, 'location', locationSearch);

        const radiusToPersist =
          localRadius ?? radius ?? radiusOptions[0] ?? null;
        if (radiusToPersist) {
          setRadius(radiusToPersist);
          updateUrlWithParams(
            accountSlug,
            brandId,
            'radius',
            radiusToPersist.key,
          );
        }
      } else {
        updateUrlWithParams(accountSlug, brandId, 'location', 'all');
        updateUrlWithParams(accountSlug, brandId, 'radius', '');
      }

      updateUrlWithParams(accountSlug, brandId, 'page', '1');
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleRadiusChange = (value: string) => {
    const selectedOption =
      radiusOptions.find(opt => opt.key === value) ?? radiusOptions[0];
    setLocalRadius(selectedOption);
    setRadius(selectedOption);

    if (accountSlug && locationSearch) {
      if (locationSearch !== location.value) {
        setLocation({
          id: selectedLocationId,
          value: locationSearch,
        });
        setIsJobsFromCurrentLocation(false);
      }

      if (jobSearch && jobSearch !== jobSearchQuery) {
        setJobSearchQuery(jobSearch);
      }

      updateUrlWithParams(accountSlug, brandId, 'radius', selectedOption.key);
      setPage(1);

      if (jobSearch) {
        updateUrlWithParams(accountSlug, brandId, 'query', jobSearch);
      }
      updateUrlWithParams(accountSlug, brandId, 'location', locationSearch);
    }
  };

  return (
    <div className="w-full">
      <div className="xs:items-center flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-center">
        {isLoading ? (
          <>
            <div className="xs:max-w-[311px] relative z-30 w-full lg:w-[311px] lg:max-w-none lg:flex-none">
              <div className="flex h-12 w-full animate-pulse items-center gap-3 border bg-gray-100 px-4 py-3">
                <div className="h-5 w-5 rounded bg-gray-300"></div>
                <div className="h-4 flex-1 rounded bg-gray-300"></div>
              </div>
            </div>

            <div className="xs:max-w-[480px] flex w-full min-w-0 items-center gap-3 lg:w-auto lg:max-w-none lg:flex-none">
              <div className="relative z-30 min-w-0 flex-1 lg:max-w-[437px] lg:flex-none">
                <div className="flex h-12 min-w-0 animate-pulse border bg-gray-100 lg:w-[437px]">
                  <div className="flex flex-1 items-center gap-2 border-r border-gray-300 px-3 py-3 lg:w-[311px] lg:flex-none">
                    <div className="h-5 w-5 rounded bg-gray-300"></div>
                    <div className="h-4 flex-1 rounded bg-gray-300"></div>
                  </div>
                  <div className="flex w-[126px] shrink-0 items-center px-4 py-3">
                    <div className="h-4 w-full rounded bg-gray-300"></div>
                  </div>
                </div>
              </div>
              <div className="xs:block hidden">
                <SearchButton onClick={() => {}} disabled />
              </div>
            </div>

            <div className="xs:hidden w-full">
              <SearchButton onClick={() => {}} disabled fullWidth />
            </div>
          </>
        ) : (
          <>
            <JobSearchInput
              jobSearch={jobSearch}
              setJobSearch={setJobSearch}
              openJobPopover={openJobPopover}
              setOpenJobPopover={setOpenJobPopover}
              jobInputRef={jobInputRef}
              onClear={clearJobSearch}
              onKeyDown={handleKeyPress}
              onJobSelect={handleJobSelect}
              scrollToJobs={scrollToJobs}
            />

            <div className="xs:max-w-[480px] flex w-full min-w-0 items-center gap-3 lg:w-auto lg:max-w-none lg:flex-none">
              <div
                className={`relative min-w-0 flex-1 lg:max-w-[437px] lg:flex-none ${
                  openLocationPopover ? 'z-40' : 'z-30'
                }`}
              >
                <div className="flex h-12 min-w-0 border bg-white lg:w-[437px]">
                  <LocationInput
                    locationSearch={locationSearch}
                    setLocationSearch={setLocationSearch}
                    openLocationPopover={openLocationPopover}
                    setOpenLocationPopover={setOpenLocationPopover}
                    locationInputRef={locationInputRef}
                    onClear={clearLocationSearch}
                    onKeyDown={handleKeyPress}
                    onLocationSelect={handleLocationSelect}
                    scrollToJobs={scrollToJobs}
                    specialLocations={specialLocations}
                  />

                  <RadiusSelect
                    localRadius={localRadius}
                    radiusOptions={radiusOptions}
                    locationSearch={locationSearch}
                    onRadiusChange={handleRadiusChange}
                    isRemoteLocation={
                      locationSearch
                        ? remoteKeywords.some(
                            keyword => locationSearch.toLowerCase() === keyword,
                          )
                        : location.value?.toLowerCase() === 'remote'
                    }
                  />
                </div>
              </div>
              <div className="xs:block hidden">
                <SearchButton onClick={handleSearch} />
              </div>
            </div>

            <div className="xs:hidden w-full">
              <SearchButton onClick={handleSearch} fullWidth />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
